// Printing that owes nothing to the page it was started from.
//
// The app used to print by hiding itself and letting the document's own @page
// rule fight whatever else had declared one — fine for a screenshot, hopeless for
// a 50×25mm sticker roll, where a stray margin or an inherited stylesheet costs a
// label per print. Everything here goes into a fresh document inside a hidden
// iframe: its own @page, its own CSS, nothing inherited, nothing to disable.
//
// The iframe is same-origin and torn down afterwards, so nothing accumulates.

const waitForAssets = (doc) => {
  const images = Array.from(doc.images || []);
  if (!images.length) return Promise.resolve();
  return Promise.all(images.map((img) => (img.complete
    ? Promise.resolve()
    // A broken logo must not hold the print dialog hostage.
    : new Promise((res) => { img.onload = res; img.onerror = res; }))));
};

// body: HTML string. css: the whole stylesheet for that document, @page included.
export function printDocument({ body, css, title = 'Print' }) {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(frame);

    const doc = frame.contentDocument || frame.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${css}</style></head><body>${body}</body></html>`);
    doc.close();

    const cleanUp = () => {
      // Chrome returns from print() immediately while the dialog is still up;
      // pulling the iframe out from under it prints a blank page.
      setTimeout(() => { try { frame.remove(); } catch (e) { /* already gone */ } resolve(); }, 1000);
    };

    const go = () => {
      waitForAssets(doc).then(() => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
        } catch (e) { /* a blocked print is the browser's call, not an app error */ }
        cleanUp();
      });
    };

    if (doc.readyState === 'complete') setTimeout(go, 60);
    else frame.onload = () => setTimeout(go, 60);
  });
}

export default printDocument;
